import {
  ETypeMainHead,
  CTypeCardView,
  DTypeBreadCrumb,
  colors,
  ATypeNationalMeritSummary,
  FullWidthTwinButton,
  GOI_WEB_URL,
  ATypeNationalMeritDescription,
  FitWidthSmallButton,
  customEvent,
  useCrematoriumInquiryModal,
  getCrematoriumPhoneByCompanyname,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";

import React from "react";
import { ParlorDetailProps } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

import crematoriums from "goi_common/src/constants/hwajang_detail.json";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = crematoriums
    .filter((p) => targetSidos.includes(String(p.sido)))
    .map((crema) => ({
      params: {
        companyname: crema.companyname,
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const companyname = params?.companyname;
  let crematorium;
  let district;
  let name = "";
  let description = "";
  
  if (companyname) {
    crematorium = crematoriums.find(
      (crema) => crema.companyname === companyname
    );
    if (crematorium) {
      name = crematorium.companyname;

      district = crematorium.address.slice(0, 2);
      if (crematorium.address.split(" ")[0].length == 4)
        district = crematorium.address[0] + crematorium.address[2];

      description = `수도권 국가유공자 화장 비용 전액 감면을 위해선 국가유공자임을 증명하는 국가유공자증이 필요합니다. 국가유공자증은 주소지 관할 보훈청 보상과에서 발급 가능하며, 자세한 혜택은 상담을 통해 안내받을 수 있습니다.`;
    }
  }

  return {
    props: {
      defaultCrema: crematorium,
      pageDescription: description,
      pageKeywords: `${crematorium?.companyname}, ${crematorium?.companyname} 국가유공자, ${district} 국가유공자 장례, ${district} 국가유공자 화장 비용, ${district} 국가유공자 장례 혜택`,
      pageOgTitle: `${crematorium?.companyname} 국가유공자를 위한 화장 비용 안내 | 서울경기인천장례정보포털`,
      pageOgDescription: description,
      pageOgImage: crematorium?.images[0].url || "",
      hasError: !crematorium,
    },
  };
};

export default function VeteransCrematoriumPage({
  defaultCrema,
  pageOgTitle,
}: {
  defaultCrema: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionCrematoriums = crematoriums.filter((p) =>
    targetSidos.includes(String(p.sido))
  );

  /** 문의 모달의 개장·이장 / 고인 확인·방문이 거는 번호. 원본에 없으면 null 이고 고이로 폴백한다. */
  const facilityPhone = getCrematoriumPhoneByCompanyname(
    defaultCrema.companyname
  );
  const { openInquiryModal } = useCrematoriumInquiryModal({
    eventDomain: "metro",
    eventPath: "veterans_crema",
  });
  const carouselItems = regionCrematoriums.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://www.metropolitan-funeral.co.kr/parlor-places/${res.id}/`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionCrematoriums.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};
  return (
    <Stack>
      <Head>
        <title>{pageOgTitle}</title>
        {regionCrematoriums.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>

      <DTypeBreadCrumb
        mainColor={regionColor}
        firstItem={{
          text: `수도권 국가유공자 장례114`,
          href: `/veterans/`,
          event: {
            domain: "metro",
            path: "veterans_crema",
            trigger_and_target: "br_crumb_veterans",
          },
        }}
        goiHomeEvent={{
          domain: "metro",
          path: "veterans_crema",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />

      <ETypeMainHead
        mainColor={regionColor}
        defaultParlor={defaultCrema}
        title={`${defaultCrema.companyname}`}
        managed_type={defaultCrema.managed_type}
        address={defaultCrema.address}
      />

      <Stack padding="20px" gap="12px">
        <Box className="subtitle_14_b" textAlign="center">
          상담을 통해 자세한 국가 유공자 장례 혜택을 안내드립니다.
        </Box>
        <FullWidthTwinButton
          firstBtn={{
            isGoiCall: true,
            text: "상담하기",
            onClick: () => {
              openInquiryModal(facilityPhone);
              customEvent({
                domain: "metro",
                path: "veterans_crema",
                trigger_and_target: "goi_call",
              });
            },
          }}
          secondBtn={{
            text: "비용 알아보기",
            href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
            onClick: () => {
              customEvent({
                domain: "metro",
                path: "veterans_crema",
                trigger_and_target: "uv_mth_req",
              });
            },
          }}
          backgroundColor={regionColor.c400}
        />
        <ATypeNationalMeritSummary mainColor={regionColor} />
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          padding="16px 20px"
          gap="4px"
          width="100%"
          alignItems="center"
        >
          <Stack flexDirection="row" gap="8px">
            <Box className="subtitle_14_sb" style={{ color: regionColor.c300 }}>
              관내 국가유공자
            </Box>
            <Box className="subtitle_14_b">화장 시설 무료</Box>
          </Stack>
          <FitWidthSmallButton
            backgroundColor={regionColor.c300}
            text="문의하기"
            href={GOI_WEB_URL + "/match/request/?utm_source=dogsound"}
            event={{
              domain: "metro",
              path: "veterans_crema",
              trigger_and_target: "uv_mth_req_btom",
            }}
          />
        </Stack>
        <ATypeNationalMeritDescription />
      </Stack>

      <Box padding="0 20px">
        <Box
          width="100%"
          height="1px"
          style={{ background: colors.gray900 }}
          margin="0 0 20px"
          padding="0 20px"
        />
      </Box>

      <Stack padding="0px 20px" marginBottom="40px" gap="8px">
        <h3 className="subtitle_18_b" style={{ textAlign: "center" }}>
          서울 · 경기 · 인천 지역 화장장
        </h3>
        {regionCrematoriums.map((res) => (
          <CTypeCardView
            sigungu={String(res.sigungu)}
            mainColor={regionColor}
            key={res.companyname}
            name={res.companyname}
            href={`/veterans/hwajang/${res.companyname}/`}
            sido={String(res.sido)}
            descriptions={["국가유공자 무료"]}
            imgSrc={res.images[0].url ? res.images[0].url : ""}
            event={{
              domain: "metro",
              path: "veterans_crema",
              trigger_and_target: "uv_crema_detail",
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}
